import React, { useMemo } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../services/api';
import moment from 'moment-timezone';

const AlertsPage: React.FC = () => {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['monitoringAlerts'],
    queryFn: () => apiClient.monitoring.getAlerts(),
    refetchInterval: 30000,
  });

  const { data: workforceData, isLoading: workforceLoading } = useQuery({
    queryKey: ['workforceAlerts', 'OPEN'],
    queryFn: () => apiClient.monitoring.listWorkforceAlerts({ status: 'OPEN' }),
    refetchInterval: 30000,
  });

  const evaluateMutation = useMutation({
    mutationFn: () => apiClient.monitoring.evaluateWorkforceAlerts(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workforceAlerts'] });
      queryClient.invalidateQueries({ queryKey: ['monitoringAlerts'] });
    },
  });

  const alerts = data?.data?.data;
  const workforceAlerts = useMemo(
    () => (workforceData?.data?.data || []) as Array<{
      id: string;
      alertType: string;
      severity: string;
      employeeId?: string | null;
      deviceId?: string | null;
      detectedAt: string;
      status: string;
      metadataJson?: string | null;
    }>,
    [workforceData],
  );

  if (isLoading || workforceLoading) return <CircularProgress size={24} />;

  return (
    <Box display="grid" gap={2}>
      <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
        <Typography variant="h6">Workforce Alerts</Typography>
        <Button
          size="small"
          variant="outlined"
          onClick={() => evaluateMutation.mutate()}
          disabled={evaluateMutation.isPending}
        >
          {evaluateMutation.isPending ? 'Evaluating…' : 'Evaluate now'}
        </Button>
      </Box>

      <Card variant="outlined">
        <CardContent>
          <Typography variant="h6" gutterBottom>Open catalog alerts</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Deduped operational alerts (login, clock-in, idle, companion, queue, emergency, Call QA, remote support).
          </Typography>
          {workforceAlerts.length === 0 ? (
            <Typography variant="body2" color="text.secondary">No open workforce alerts.</Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Detected</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Severity</TableCell>
                  <TableCell>Employee</TableCell>
                  <TableCell>Device</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {workforceAlerts.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>{moment(row.detectedAt).format('YYYY-MM-DD HH:mm')}</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{row.alertType}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        color={row.severity === 'critical' ? 'error' : row.severity === 'warning' ? 'warning' : 'default'}
                        label={row.severity}
                      />
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{row.employeeId || '—'}</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{row.deviceId || '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardContent>
          <Typography variant="h6" gutterBottom>Needs Attention</Typography>
          <List dense>
            {(alerts?.needs_attention || []).map((item: any) => (
              <ListItem key={item.key}>
                <ListItemText primary={item.label} secondary={`Severity: ${item.severity}`} />
                <Chip size="small" label={item.count} />
              </ListItem>
            ))}
          </List>
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardContent>
          <Typography variant="h6" gutterBottom>Campaign Mismatches</Typography>
          {(alerts?.campaign_mismatches || []).length === 0 ? (
            <Typography variant="body2" color="text.secondary">No campaign mismatches detected today.</Typography>
          ) : (
            (alerts?.campaign_mismatches || []).map((row: any) => (
              <Alert key={`${row.employee_id}-${row.detected_at}`} severity="warning" sx={{ mb: 1 }}>
                {row.employee_name}: recorded {row.recorded_campaign_name}, expected {row.expected_campaign_name || 'none'} — {row.reason}
              </Alert>
            ))
          )}
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardContent>
          <Typography variant="h6" gutterBottom>Emergency Bypass (24h)</Typography>
          {(alerts?.emergency_events || []).length === 0 ? (
            <Typography variant="body2" color="text.secondary">No emergency bypass events in the last 24 hours.</Typography>
          ) : (
            (alerts?.emergency_events || []).map((event: any) => (
              <Typography key={event.id} variant="body2" sx={{ fontFamily: 'monospace' }}>
                {moment(event.event_timestamp).format('HH:mm:ss')} {event.event_type} {event.firstName} {event.lastName}
              </Typography>
            ))
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default AlertsPage;
